// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useHotelPartnerId } from './hooks/useHotelPartnerId';
import { useUpdateBooking, TeamRow } from './hooks/useEventTeamTracker';

interface Props {
  open: boolean;
  onClose: () => void;
  team: TeamRow;
  eventId: string;
}

export const ViewEditBookingModal: React.FC<Props> = ({ open, onClose, team, eventId }) => {
  const { data: partnerId } = useHotelPartnerId();
  const updateBooking = useUpdateBooking(eventId);
  const [editing, setEditing] = useState(false);

  const { data: booking, isLoading } = useQuery({
    queryKey: ['hotel-booking-detail', team.booking_id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_bookings')
        .select('*')
        .eq('id', team.booking_id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!team.booking_id && !!partnerId,
  });

  const [form, setForm] = useState({
    hotel_name: '',
    confirmation_number: '',
    check_in_date: '',
    check_out_date: '',
    number_of_rooms: '1',
    rate_per_night: '',
    status: 'confirmed',
    notes: '',
  });

  useEffect(() => {
    if (booking) {
      setForm({
        hotel_name: booking.hotel_name || '',
        confirmation_number: booking.confirmation_number || '',
        check_in_date: booking.check_in_date || '',
        check_out_date: booking.check_out_date || '',
        number_of_rooms: String(booking.number_of_rooms || 1),
        rate_per_night: String(booking.rate_per_night || ''),
        status: booking.status || 'confirmed',
        notes: booking.notes || '',
      });
    }
  }, [booking]);

  const handleSave = async () => {
    if (!team.booking_id) return;
    try {
      // room_nights / gross_revenue are trigger-computed — not sent.
      await updateBooking.mutateAsync({
        bookingId: team.booking_id,
        hotel_name: form.hotel_name,
        confirmation_number: form.confirmation_number,
        check_in_date: form.check_in_date,
        check_out_date: form.check_out_date,
        number_of_rooms: parseInt(form.number_of_rooms) || 1,
        rate_per_night: parseFloat(form.rate_per_night) || 0,
        status: form.status,
        notes: form.notes,
      });
      toast.success('Booking updated');
      setEditing(false);
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update booking');
    }
  };

  const set = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? 'Edit Booking' : 'Booking Details'} — {team.team_name}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3 py-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : !booking ? (
          <p className="py-4 text-muted-foreground">Booking not found.</p>
        ) : !editing ? (
          <div className="space-y-3 py-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Hotel:</span> {booking.hotel_name}</div>
              <div><span className="text-muted-foreground">Conf #:</span> {booking.confirmation_number}</div>
              <div><span className="text-muted-foreground">Check-in:</span> {booking.check_in_date}</div>
              <div><span className="text-muted-foreground">Check-out:</span> {booking.check_out_date}</div>
              <div><span className="text-muted-foreground">Rooms:</span> {booking.number_of_rooms}</div>
              <div><span className="text-muted-foreground">Rate:</span> ${Number(booking.rate_per_night).toFixed(2)}/night</div>
              <div><span className="text-muted-foreground">Room Nights:</span> {booking.room_nights ?? '—'}</div>
              <div><span className="text-muted-foreground">Gross Revenue:</span> {booking.gross_revenue != null ? `$${Number(booking.gross_revenue).toFixed(2)}` : '—'}</div>
              <div><span className="text-muted-foreground">Status:</span> <span className="capitalize">{booking.status}</span></div>
            </div>
            {booking.notes && (
              <div>
                <span className="text-muted-foreground">Notes:</span> {booking.notes}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Hotel Name</Label>
              <Input value={form.hotel_name} onChange={(e) => set('hotel_name', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Confirmation #</Label>
              <Input value={form.confirmation_number} onChange={(e) => set('confirmation_number', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Check-in</Label>
                <Input type="date" value={form.check_in_date} onChange={(e) => set('check_in_date', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Check-out</Label>
                <Input type="date" value={form.check_out_date} onChange={(e) => set('check_out_date', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Rooms</Label>
                <Input type="number" min="1" value={form.number_of_rooms} onChange={(e) => set('number_of_rooms', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Rate/Night</Label>
                <Input type="number" min="0" step="0.01" value={form.rate_per_night} onChange={(e) => set('rate_per_night', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="exempt">Exempt</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} />
            </div>
          </div>
        )}

        <DialogFooter>
          {!editing ? (
            <>
              <Button variant="outline" onClick={onClose}>Close</Button>
              {booking && (
                <Button onClick={() => setEditing(true)}>Edit</Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={updateBooking.isPending}>
                {updateBooking.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
